import React, { useState, useEffect } from "react";
import api from "@/services/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  DollarSign,
  Users,
  CheckCircle,
  Clock,
  CreditCard,
  Building,
  Search,
  Calendar,
  TrendingUp,
  Wallet,
  ChevronDown,
  ChevronUp,
  Send,
} from "lucide-react";

export default function AdminNomina() {
  const [activeTab, setActiveTab] = useState("por-pagar");
  const [loading, setLoading] = useState(true);
  const [porPagar, setPorPagar] = useState(null);
  const [historial, setHistorial] = useState(null);
  const [resumen, setResumen] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [expandedVendedor, setExpandedVendedor] = useState(null);
  
  // Filtros de fecha
  const hoy = new Date().toISOString().split("T")[0];
  const inicioSemana = new Date(Date.now() - new Date().getDay() * 86400000).toISOString().split("T")[0];
  const [fechaInicio, setFechaInicio] = useState(inicioSemana);
  const [fechaFin, setFechaFin] = useState(hoy);
  
  // Filtro de estado de pago
  const [filtroEstado, setFiltroEstado] = useState("todos"); // todos, pendiente, pagado
  
  // Modal de pago
  const [pagoDialogOpen, setPagoDialogOpen] = useState(false);
  const [selectedVendedor, setSelectedVendedor] = useState(null);
  const [pagoForm, setPagoForm] = useState({
    metodo_pago: "transferencia",
    referencia: "",
    notas: ""
  });
  const [submittingPago, setSubmittingPago] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    // Refetch when dates change
    if (fechaInicio && fechaFin) {
      fetchPorPagar();
    }
  }, [fechaInicio, fechaFin]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [porPagarRes, historialRes, resumenRes] = await Promise.all([
        api.get(`/nomina/admin/por-pagar?fecha_inicio=${fechaInicio}&fecha_fin=${fechaFin}`),
        api.get(`/nomina/admin/historial-pagos?fecha_inicio=${fechaInicio}&fecha_fin=${fechaFin}`),
        api.get("/nomina/admin/resumen")
      ]);
      setPorPagar(porPagarRes.data);
      setHistorial(historialRes.data);
      setResumen(resumenRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Error al cargar datos de nómina");
    } finally {
      setLoading(false);
    }
  };

  const fetchPorPagar = async () => {
    try {
      const res = await api.get(`/nomina/admin/por-pagar?fecha_inicio=${fechaInicio}&fecha_fin=${fechaFin}`);
      setPorPagar(res.data);
    } catch (error) {
      console.error("Error fetching por pagar:", error);
    }
  };

  const fetchHistorial = async () => {
    try {
      const res = await api.get(`/nomina/admin/historial-pagos?fecha_inicio=${fechaInicio}&fecha_fin=${fechaFin}`);
      setHistorial(res.data);
    } catch (error) {
      console.error("Error fetching historial:", error);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("es-CO", {
      style: "currency",
      currency: "COP",
      minimumFractionDigits: 0,
    }).format(value || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("es-CO", {
      day: "numeric",
      month: "short",
      year: "numeric"
    });
  };

  const handleOpenPagoDialog = (vendedor) => {
    setSelectedVendedor(vendedor);
    setPagoForm({
      metodo_pago: "transferencia",
      referencia: "",
      notas: ""
    });
    setPagoDialogOpen(true);
  };

  const handleRegistrarPago = async (e) => {
    e.preventDefault();
    if (!selectedVendedor) return;
    
    setSubmittingPago(true);
    try {
      await api.post("/nomina/admin/registrar-pago", {
        vendedor_id: selectedVendedor.vendedor_id,
        monto: selectedVendedor.total_comision,
        periodo_inicio: selectedVendedor.periodo_inicio,
        periodo_fin: selectedVendedor.periodo_fin,
        metodo_pago: pagoForm.metodo_pago,
        referencia: pagoForm.referencia || null,
        notas: pagoForm.notas || null
      });
      
      toast.success(`Pago registrado para ${selectedVendedor.vendedor_nombre}`);
      setPagoDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al registrar pago");
    } finally {
      setSubmittingPago(false);
    }
  };

  const filteredVendedores = porPagar?.vendedores?.filter(v => {
    const matchSearch = v.vendedor_nombre?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      v.zona?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchEstado = filtroEstado === "todos" || v.estado_pago === filtroEstado;
    return matchSearch && matchEstado;
  }) || [];

  const filteredHistorial = historial?.pagos?.filter(p =>
    p.vendedor_nombre?.toLowerCase().includes(searchTerm.toLowerCase())
  ) || [];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-pulse text-torta-brown">Cargando...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-nomina">
      <div>
        <h1 className="font-display text-3xl text-torta-dark">Nómina y Pagos</h1>
        <p className="text-torta-muted">Gestiona los pagos de comisiones a vendedores</p>
      </div>

      {/* Resumen Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-gradient-to-br from-amber-500 to-orange-500 rounded-2xl p-5 text-white">
          <div className="flex items-center justify-between mb-3">
            <span className="text-white/80 text-sm">Por Pagar (Semana)</span>
            <Clock className="w-5 h-5 text-white/60" />
          </div>
          <p className="font-display text-3xl">{formatCurrency(resumen?.por_pagar?.total)}</p>
          <p className="text-white/70 text-sm mt-1">
            {resumen?.por_pagar?.cantidad_vendedores || 0} vendedores
          </p>
        </div>

        <div className="bg-gradient-to-br from-green-500 to-emerald-500 rounded-2xl p-5 text-white">
          <div className="flex items-center justify-between mb-3">
            <span className="text-white/80 text-sm">Pagado esta Semana</span>
            <CheckCircle className="w-5 h-5 text-white/60" />
          </div>
          <p className="font-display text-3xl">{formatCurrency(resumen?.pagado_semana?.total)}</p>
          <p className="text-white/70 text-sm mt-1">
            {resumen?.pagado_semana?.cantidad || 0} pagos
          </p>
        </div>

        <div className="bg-gradient-to-br from-blue-500 to-indigo-500 rounded-2xl p-5 text-white">
          <div className="flex items-center justify-between mb-3">
            <span className="text-white/80 text-sm">Pagado este Mes</span>
            <TrendingUp className="w-5 h-5 text-white/60" />
          </div>
          <p className="font-display text-3xl">{formatCurrency(resumen?.pagado_mes?.total)}</p>
          <p className="text-white/70 text-sm mt-1">
            {resumen?.pagado_mes?.cantidad || 0} pagos
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-torta-sand">
        <button
          onClick={() => setActiveTab("por-pagar")}
          className={`px-4 py-3 font-medium text-sm transition-colors relative ${
            activeTab === "por-pagar"
              ? "text-torta-brown"
              : "text-torta-muted hover:text-torta-dark"
          }`}
        >
          <span className="flex items-center gap-2">
            <Clock className="w-4 h-4" />
            Por Pagar
            {porPagar?.cantidad_vendedores > 0 && (
              <span className="bg-amber-500 text-white text-xs px-2 py-0.5 rounded-full">
                {porPagar.cantidad_vendedores}
              </span>
            )}
          </span>
          {activeTab === "por-pagar" && (
            <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-torta-brown" />
          )}
        </button>
        <button
          onClick={() => setActiveTab("historial")}
          className={`px-4 py-3 font-medium text-sm transition-colors relative ${
            activeTab === "historial"
              ? "text-torta-brown"
              : "text-torta-muted hover:text-torta-dark"
          }`}
        >
          <span className="flex items-center gap-2">
            <CheckCircle className="w-4 h-4" />
            Historial de Pagos
          </span>
          {activeTab === "historial" && (
            <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-torta-brown" />
          )}
        </button>
      </div>

      {/* Search and Filters */}
      <div className="bg-white rounded-xl p-4 shadow-sm space-y-4">
        {/* Date Selectors */}
        <div className="flex flex-wrap items-end gap-4">
          <div className="flex-1 min-w-[150px]">
            <Label className="text-sm text-torta-muted mb-1 flex items-center gap-1">
              <Calendar className="w-3 h-3" /> Fecha Inicio
            </Label>
            <Input
              type="date"
              value={fechaInicio}
              onChange={(e) => setFechaInicio(e.target.value)}
              className="h-10"
            />
          </div>
          <div className="flex-1 min-w-[150px]">
            <Label className="text-sm text-torta-muted mb-1 flex items-center gap-1">
              <Calendar className="w-3 h-3" /> Fecha Fin
            </Label>
            <Input
              type="date"
              value={fechaFin}
              onChange={(e) => setFechaFin(e.target.value)}
              className="h-10"
            />
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                const hoy = new Date();
                const inicioSemana = new Date(hoy);
                inicioSemana.setDate(hoy.getDate() - hoy.getDay());
                setFechaInicio(inicioSemana.toISOString().split("T")[0]);
                setFechaFin(hoy.toISOString().split("T")[0]);
              }}
            >
              Esta Semana
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                const hoy = new Date();
                const inicioMes = new Date(hoy.getFullYear(), hoy.getMonth(), 1);
                setFechaInicio(inicioMes.toISOString().split("T")[0]);
                setFechaFin(hoy.toISOString().split("T")[0]);
              }}
            >
              Este Mes
            </Button>
          </div>
        </div>
        
        {/* Search and Status Filter */}
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex-1 min-w-[200px] relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-torta-muted" />
            <Input
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Buscar por nombre o zona..."
              className="pl-10 h-10"
            />
          </div>
          
          {/* Status Filter */}
          <div className="flex gap-2">
            <button
              onClick={() => setFiltroEstado("todos")}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                filtroEstado === "todos"
                  ? "bg-torta-brown text-white"
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}
            >
              Todos
            </button>
            <button
              onClick={() => setFiltroEstado("pendiente")}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                filtroEstado === "pendiente"
                  ? "bg-amber-500 text-white"
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}
            >
              Pendientes
            </button>
            <button
              onClick={() => setFiltroEstado("pagado")}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                filtroEstado === "pagado"
                  ? "bg-green-500 text-white"
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}
            >
              Pagados
            </button>
          </div>
        </div>
      </div>

      {/* Tab Content */}
      {activeTab === "por-pagar" && (
        <div className="space-y-4">
          {/* Period Info */}
          {porPagar?.periodo && (
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-center gap-3">
              <Calendar className="w-5 h-5 text-blue-600" />
              <p className="text-blue-800 text-sm">
                <strong>Período:</strong> {formatDate(porPagar.periodo.inicio)} - {formatDate(porPagar.periodo.fin)}
              </p>
            </div>
          )}

          {filteredVendedores.length === 0 ? (
            <div className="bg-white rounded-2xl p-8 text-center">
              <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
              <p className="text-torta-dark font-medium">No hay pagos pendientes</p>
              <p className="text-torta-muted text-sm">Todos los vendedores están al día</p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredVendedores.map((vendedor) => (
                <div
                  key={vendedor.vendedor_id}
                  className="bg-white rounded-2xl shadow-sm overflow-hidden"
                >
                  {/* Header */}
                  <div 
                    className="p-4 flex items-center gap-4 cursor-pointer hover:bg-gray-50"
                    onClick={() => setExpandedVendedor(
                      expandedVendedor === vendedor.vendedor_id ? null : vendedor.vendedor_id
                    )}
                  >
                    <div className="w-12 h-12 bg-torta-cream rounded-full flex items-center justify-center font-display text-torta-brown text-lg">
                      {vendedor.vendedor_nombre?.charAt(0)}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-torta-dark">{vendedor.vendedor_nombre}</p>
                        <span className={`px-2 py-0.5 rounded-full text-xs ${
                          vendedor.tipo_vendedor === "digital" 
                            ? "bg-blue-100 text-blue-700" 
                            : "bg-amber-100 text-amber-700"
                        }`}>
                          {vendedor.tipo_vendedor}
                        </span>
                      </div>
                      <p className="text-torta-muted text-sm">{vendedor.zona || "Sin zona"}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-display text-xl text-torta-dark">
                        {formatCurrency(vendedor.total_comision)}
                      </p>
                      <p className="text-torta-muted text-xs">
                        {vendedor.unidades_vendidas} unidades
                      </p>
                      {vendedor.estado_pago === "pagado" && (
                        <span className="inline-flex items-center gap-1 text-xs text-green-600 bg-green-100 px-2 py-0.5 rounded-full mt-1">
                          <CheckCircle className="w-3 h-3" /> Pagado
                        </span>
                      )}
                    </div>
                    {expandedVendedor === vendedor.vendedor_id ? (
                      <ChevronUp className="w-5 h-5 text-torta-muted" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-torta-muted" />
                    )}
                  </div>

                  {/* Expanded Details */}
                  {expandedVendedor === vendedor.vendedor_id && (
                    <div className="px-4 pb-4 border-t border-torta-sand">
                      <div className="grid md:grid-cols-2 gap-4 mt-4">
                        {/* Desglose */}
                        <div className="space-y-2">
                          <p className="text-sm font-medium text-torta-dark">Desglose de Comisión</p>
                          <div className="bg-torta-cream/50 rounded-xl p-3 space-y-2 text-sm">
                            <div className="flex justify-between">
                              <span className="text-torta-muted">Comisión base</span>
                              <span>{formatCurrency(vendedor.desglose?.comision_base)}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-torta-muted">Bono escala</span>
                              <span>{formatCurrency(vendedor.desglose?.bono_escala)}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-torta-muted">Bono portafolio</span>
                              <span>{formatCurrency(vendedor.desglose?.bono_portafolio)}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-torta-muted">Bono zona</span>
                              <span>{formatCurrency(vendedor.desglose?.bono_zona)}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-torta-muted">Apoyo transporte</span>
                              <span>{formatCurrency(vendedor.desglose?.apoyo_transporte)}</span>
                            </div>
                            <div className="flex justify-between border-t border-torta-sand pt-2 font-medium">
                              <span>Total</span>
                              <span>{formatCurrency(vendedor.total_comision)}</span>
                            </div>
                          </div>
                          
                          {/* Resumen de ventas del período */}
                          {vendedor.ventas_detalle && vendedor.ventas_detalle.length > 0 && (
                            <div className="mt-4">
                              <p className="text-sm font-medium text-torta-dark mb-2">Ventas del Período</p>
                              <div className="bg-white border border-torta-sand rounded-xl max-h-48 overflow-y-auto">
                                {vendedor.ventas_detalle.map((venta, idx) => (
                                  <div key={idx} className="p-2 border-b border-torta-sand/50 last:border-b-0 text-xs">
                                    <div className="flex items-center justify-between">
                                      <span className={`px-1.5 py-0.5 rounded text-[10px] ${
                                        venta.tipo === 'directa' ? 'bg-green-100 text-green-700' :
                                        venta.tipo === 'tercero' ? 'bg-blue-100 text-blue-700' :
                                        'bg-purple-100 text-purple-700'
                                      }`}>
                                        {venta.tipo === 'directa' ? 'Directa' : 
                                         venta.tipo === 'tercero' ? 'Tercero' : 'Online'}
                                      </span>
                                      <span className="text-torta-muted">{new Date(venta.fecha).toLocaleDateString('es-CO')}</span>
                                      <span className="font-medium">{formatCurrency(venta.total)}</span>
                                    </div>
                                    <p className="text-torta-muted mt-1 truncate">{venta.cliente}</p>
                                    <div className="flex flex-wrap gap-1 mt-1">
                                      {venta.items?.map((item, i) => (
                                        <span key={i} className="bg-torta-cream px-1.5 py-0.5 rounded text-[10px]">
                                          {item.cantidad}x {item.producto_nombre}
                                        </span>
                                      ))}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Datos bancarios completos */}
                        <div className="space-y-2">
                          <p className="text-sm font-medium text-torta-dark">Datos de Pago</p>
                          <div className="bg-torta-cream/50 rounded-xl p-3 space-y-2 text-sm">
                            <div className="flex items-center gap-2">
                              <Calendar className="w-4 h-4 text-torta-muted" />
                              <span className="text-torta-muted">Frecuencia:</span>
                              <span className="capitalize">{vendedor.frecuencia_pago || "semanal"}</span>
                            </div>
                            {vendedor.banco ? (
                              <>
                                <div className="flex items-center gap-2">
                                  <Building className="w-4 h-4 text-torta-muted" />
                                  <span className="text-torta-muted">Banco:</span>
                                  <span className="capitalize font-medium">{vendedor.banco}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                  <CreditCard className="w-4 h-4 text-torta-muted" />
                                  <span className="text-torta-muted">Tipo:</span>
                                  <span className="capitalize">{vendedor.tipo_cuenta || "-"}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                  <CreditCard className="w-4 h-4 text-torta-muted" />
                                  <span className="text-torta-muted">Número:</span>
                                  <span className="font-mono font-medium">{vendedor.numero_cuenta || "-"}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                  <Users className="w-4 h-4 text-torta-muted" />
                                  <span className="text-torta-muted">Titular:</span>
                                  <span>{vendedor.titular_cuenta || "-"}</span>
                                </div>
                              </>
                            ) : (
                              <div className="bg-amber-50 border border-amber-200 rounded-lg p-2">
                                <p className="text-amber-700 text-xs">
                                  Sin datos bancarios registrados
                                </p>
                              </div>
                            )}
                            
                            {/* Contacto */}
                            <div className="border-t border-torta-sand pt-2 mt-2">
                              {vendedor.telefono && (
                                <div className="flex items-center gap-2">
                                  <span className="text-torta-muted">Tel:</span>
                                  <a href={`tel:${vendedor.telefono}`} className="text-blue-600 hover:underline">
                                    {vendedor.telefono}
                                  </a>
                                </div>
                              )}
                              {vendedor.email && (
                                <div className="flex items-center gap-2">
                                  <span className="text-torta-muted">Email:</span>
                                  <a href={`mailto:${vendedor.email}`} className="text-blue-600 hover:underline text-xs">
                                    {vendedor.email}
                                  </a>
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Estado de pago */}
                          {vendedor.estado_pago === "pagado" ? (
                            <div className="bg-green-50 border border-green-200 rounded-xl p-3 mt-2">
                              <div className="flex items-center gap-2 text-green-700">
                                <CheckCircle className="w-4 h-4" />
                                <span className="font-medium">Ya pagado este período</span>
                              </div>
                            </div>
                          ) : (
                            <Button
                              onClick={() => handleOpenPagoDialog(vendedor)}
                              className="w-full bg-green-600 hover:bg-green-700 mt-2"
                            >
                              <DollarSign className="w-4 h-4 mr-2" />
                              Registrar Pago
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === "historial" && (
        <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
          {filteredHistorial.length === 0 ? (
            <div className="p-8 text-center">
              <Wallet className="w-12 h-12 text-torta-muted mx-auto mb-4" />
              <p className="text-torta-dark font-medium">Sin historial de pagos</p>
              <p className="text-torta-muted text-sm">Los pagos registrados aparecerán aquí</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-torta-sand/50">
                  <tr>
                    <th className="text-left p-4 text-sm font-medium text-torta-dark">Fecha</th>
                    <th className="text-left p-4 text-sm font-medium text-torta-dark">Vendedor</th>
                    <th className="text-left p-4 text-sm font-medium text-torta-dark">Período</th>
                    <th className="text-left p-4 text-sm font-medium text-torta-dark">Método</th>
                    <th className="text-right p-4 text-sm font-medium text-torta-dark">Monto</th>
                    <th className="text-left p-4 text-sm font-medium text-torta-dark">Referencia</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-torta-sand">
                  {filteredHistorial.map((pago) => (
                    <tr key={pago.id} className="hover:bg-gray-50">
                      <td className="p-4">
                        <p className="text-sm text-torta-dark">{formatDate(pago.fecha_pago)}</p>
                      </td>
                      <td className="p-4">
                        <p className="font-medium text-torta-dark">{pago.vendedor_nombre}</p>
                      </td>
                      <td className="p-4">
                        <p className="text-sm text-torta-muted">
                          {formatDate(pago.periodo_inicio)} - {formatDate(pago.periodo_fin)}
                        </p>
                      </td>
                      <td className="p-4">
                        <span className={`px-2 py-1 rounded-full text-xs ${
                          pago.metodo_pago === "transferencia" 
                            ? "bg-blue-100 text-blue-700"
                            : "bg-green-100 text-green-700"
                        }`}>
                          {pago.metodo_pago}
                        </span>
                      </td>
                      <td className="p-4 text-right">
                        <p className="font-display text-lg text-green-600">
                          {formatCurrency(pago.monto)}
                        </p>
                      </td>
                      <td className="p-4">
                        <p className="text-sm text-torta-muted">{pago.referencia || "-"}</p>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Modal Registrar Pago */}
      <Dialog open={pagoDialogOpen} onOpenChange={setPagoDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">Registrar Pago</DialogTitle>
          </DialogHeader>

          {selectedVendedor && (
            <form onSubmit={handleRegistrarPago} className="space-y-4">
              {/* Resumen del pago */}
              <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center font-display text-green-700">
                    {selectedVendedor.vendedor_nombre?.charAt(0)}
                  </div>
                  <div>
                    <p className="font-medium text-green-800">{selectedVendedor.vendedor_nombre}</p>
                    <p className="text-green-600 text-sm">{selectedVendedor.zona}</p>
                  </div>
                </div>
                <div className="flex justify-between items-center pt-2 border-t border-green-200">
                  <span className="text-green-700">Monto a pagar:</span>
                  <span className="font-display text-2xl text-green-800">
                    {formatCurrency(selectedVendedor.total_comision)}
                  </span>
                </div>
              </div>

              {/* Método de pago */}
              <div className="space-y-2">
                <Label>Método de Pago</Label>
                <Select
                  value={pagoForm.metodo_pago}
                  onValueChange={(value) => setPagoForm({ ...pagoForm, metodo_pago: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="transferencia">Transferencia Bancaria</SelectItem>
                    <SelectItem value="efectivo">Efectivo</SelectItem>
                    <SelectItem value="nequi">Nequi</SelectItem>
                    <SelectItem value="daviplata">Daviplata</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Referencia */}
              <div className="space-y-2">
                <Label>Número de Referencia (opcional)</Label>
                <Input
                  value={pagoForm.referencia}
                  onChange={(e) => setPagoForm({ ...pagoForm, referencia: e.target.value })}
                  placeholder="Ej: TRF123456"
                />
              </div>

              {/* Notas */}
              <div className="space-y-2">
                <Label>Notas (opcional)</Label>
                <Textarea
                  value={pagoForm.notas}
                  onChange={(e) => setPagoForm({ ...pagoForm, notas: e.target.value })}
                  placeholder="Observaciones adicionales..."
                  rows={2}
                />
              </div>

              {/* Botones */}
              <div className="flex gap-3 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setPagoDialogOpen(false)}
                  className="flex-1"
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  disabled={submittingPago}
                  className="flex-1 bg-green-600 hover:bg-green-700"
                >
                  {submittingPago ? (
                    <span className="flex items-center gap-2">
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Registrando...
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <Send className="w-4 h-4" />
                      Confirmar Pago
                    </span>
                  )}
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
